import { differenceInCalendarDays, format, isBefore, isValid, parse } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { AvailabilityCalendarJob, AvailabilityCalendarSlot, AvailabilityStatus } from 'types';

export const SLOT_DATE_TIME_FORMAT = 'yyyy-MM-dd HH:mm:ss';

export const DESCRIPTION_MAX_LENGTH = 600;

const EMPTY_DATE_TIME = '0000-00-00 00:00:00';

const PENDING_RESCHEDULE_STATUSES = ['pending', 'pending_cancellation'];

const EDITABLE_STATUSES = [
  AvailabilityStatus.Available,
  AvailabilityStatus.RequiresConfirmation,
  AvailabilityStatus.Unavailable,
];

export enum SlotAppearance {
  Available = 'available',
  Unavailable = 'unavailable',
  Away = 'away',
  RequiresConfirmation = 'requires-confirmation',
  RecurringUnavailable = 'recurring-unavailable',
  RecurringRequiresConfirmation = 'recurring-requires-confirmation',
  OnsiteJob = 'onsite-job',
  RemoteJob = 'remote-job',
  PendingJob = 'pending-job',
  WarrantyJob = 'warranty-job',
  HardwareReturn = 'hardware-return',
}

const STATUS_CYCLE: Partial<Record<AvailabilityStatus, AvailabilityStatus>> = {
  [AvailabilityStatus.Available]: AvailabilityStatus.RequiresConfirmation,
  [AvailabilityStatus.RequiresConfirmation]: AvailabilityStatus.Unavailable,
  [AvailabilityStatus.Unavailable]: AvailabilityStatus.Available,
};

/**
 * The status a slot moves to when clicked: Available → Requires Confirmation → Unavailable → Available.
 */
export const nextAvailabilityStatus = (status: AvailabilityStatus): AvailabilityStatus =>
  STATUS_CYCLE[status] ?? AvailabilityStatus.RequiresConfirmation;

export const slotJob = (slot: AvailabilityCalendarSlot): AvailabilityCalendarJob | undefined =>
  Array.isArray(slot.job) ? undefined : slot.job;

/**
 * Which legend design a slot is drawn with. Jobs follow Pending > Hardware Return > Warranty > Remote / On-site.
 */
export const slotAppearance = (slot: AvailabilityCalendarSlot): SlotAppearance => {
  const job = slotJob(slot);

  if (job && PENDING_RESCHEDULE_STATUSES.includes(job.reschedule_status)) {
    return SlotAppearance.PendingJob;
  }
  if (slot.status === AvailabilityStatus.HardwareReturn) {
    return SlotAppearance.HardwareReturn;
  }
  if (slot.status === AvailabilityStatus.Warranty) {
    return SlotAppearance.WarrantyJob;
  }
  if (slot.status === AvailabilityStatus.Job) {
    return job?.type === 'R' ? SlotAppearance.RemoteJob : SlotAppearance.OnsiteJob;
  }
  if (slot.status === AvailabilityStatus.Away) {
    return SlotAppearance.Away;
  }
  if (slot.recurring && slot.status === AvailabilityStatus.RequiresConfirmation) {
    return SlotAppearance.RecurringRequiresConfirmation;
  }
  if (slot.recurring && slot.status === AvailabilityStatus.Unavailable) {
    return SlotAppearance.RecurringUnavailable;
  }
  if (slot.status === AvailabilityStatus.RequiresConfirmation) {
    return SlotAppearance.RequiresConfirmation;
  }
  if (slot.status === AvailabilityStatus.Unavailable) {
    return SlotAppearance.Unavailable;
  }

  return SlotAppearance.Available;
};

export const hasJobPopover = (slot: AvailabilityCalendarSlot): boolean =>
  !!slotJob(slot) &&
  [
    AvailabilityStatus.Job,
    AvailabilityStatus.Warranty,
    AvailabilityStatus.HardwareReturn,
  ].includes(slot.status);

/**
 * Whether the slot has started before now in the technician's timezone.
 */
export const isSlotInPast = (dateTime: string, timezone: string): boolean =>
  isBefore(parse(dateTime, SLOT_DATE_TIME_FORMAT, new Date()), toZonedTime(new Date(), timezone));

/**
 * A slot can be changed from the calendar when it is in the future, Available / Requires Confirmation / Unavailable,
 * and not coming from a recurring rule or an away period.
 */
export const isSlotEligible = (slot: AvailabilityCalendarSlot, dateTime: string, timezone: string): boolean =>
  !slot.recurring && EDITABLE_STATUSES.includes(slot.status) && !isSlotInPast(dateTime, timezone);

/**
 * Popover date format, e.g. "08:30am - 01 Apr 2025". Empty for missing or zero dates.
 */
export const formatPopoverDateTime = (value?: string | null): string => {
  if (!value || value === EMPTY_DATE_TIME) {
    return '';
  }

  const date = parse(value, SLOT_DATE_TIME_FORMAT, new Date());

  return isValid(date) ? format(date, 'hh:mmaaa - dd MMM yyyy') : '';
};

export const truncateDescription = (description: string, maxLength: number = DESCRIPTION_MAX_LENGTH): string =>
  description.length > maxLength ? `${description.slice(0, maxLength)}…` : description;

export const inclusiveDayCount = (startDate?: Date | null, endDate?: Date | null): number => {
  if (!startDate || !endDate) {
    return 0;
  }

  return Math.max(differenceInCalendarDays(endDate, startDate) + 1, 0);
};

/**
 * Convert an "HH:mm" time to the decimal hours the API stores, e.g. "09:30" → 9.5.
 */
export const timeToDecimal = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number);

  return hours + minutes / 60;
};

export const API_DATE_FORMAT = 'yyyy-MM-dd';

export const NO_END_DATE = '0000-00-00';

export const parseApiDate = (value: string): Date => parse(value, API_DATE_FORMAT, new Date());

/**
 * List date format, e.g. "Mon, 14 Apr 2027"; an open-ended period has no end date.
 */
export const formatAwayDate = (value: string): string =>
  value === NO_END_DATE ? 'No end date' : format(parseApiDate(value), 'EEE, d MMM yyyy');

export const formatDayCount = (count: number): string => `${count} ${count === 1 ? 'day' : 'days'}`;

/**
 * Convert the decimal hours the API stores to an "HH:mm" time, e.g. 9.5 → "09:30".
 */
export const decimalToTime = (value: number): string => {
  const minutes = Math.round(value * 60);

  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
};

const WEEK_DAYS = [
  { code: 'mon', label: 'Mon' },
  { code: 'tue', label: 'Tue' },
  { code: 'wed', label: 'Wed' },
  { code: 'thu', label: 'Thu' },
  { code: 'fri', label: 'Fri' },
  { code: 'sat', label: 'Sat' },
  { code: 'sun', label: 'Sun' },
];

const RECURRING_STATUS_LABELS: Record<string, string> = {
  N: 'Unavailable',
  C: 'Requires Confirmation',
};

/**
 * Effective period, e.g. "14 Apr 2027 - 16 Aug 2027"; an open-ended rule has no end date.
 */
export const formatEffectivePeriod = (startDate: string, endDate: string): string => {
  const end = endDate === NO_END_DATE ? 'No end date' : format(parseApiDate(endDate), 'd MMM yyyy');

  return `${format(parseApiDate(startDate), 'd MMM yyyy')} - ${end}`;
};

export const formatRecurringDays = (codes: string[]): string =>
  WEEK_DAYS.filter((day) => codes.includes(day.code))
    .map((day) => day.label)
    .join(', ');

export const formatDailyTiming = (startTime: number, endTime: number): string =>
  `${decimalToTime(startTime)} - ${decimalToTime(endTime)}`;

export const recurringStatusLabel = (setting: string): string => RECURRING_STATUS_LABELS[setting] ?? setting;
